import { Injectable, Logger } from '@nestjs/common';
import { ConfigService } from '@nestjs/config';
import * as nodemailer from 'nodemailer';

export interface OrderEmailData {
    orderId: string;
    orderNumber: string;
    customerName: string;
    phone: string;
    subtotal: number;
    shippingCost: number;
    tax: number;
    discount: number;
    total: number;
    amountPaid?: number;
    items: Array<{
        name: string;
        quantity: number;
        price: number;
        totalPrice: number;
    }>;
}

@Injectable()
export class EmailService {
    private readonly logger = new Logger(EmailService.name);
    private transporter: nodemailer.Transporter;
    private readonly adminEmail = this.configService.get<string>('email.user') || '';

    constructor(private configService: ConfigService) {
        this.initializeTransporter();
    }

    private initializeTransporter() {
        // Configure email transporter using environment variables
        // IMPORTANT: For Gmail, you MUST use App Password (not regular password)
        // To generate App Password:
        // 1. Enable 2-Step Verification on your Google Account
        // 2. Go to Google Account → Security → 2-Step Verification → App passwords
        // 3. Generate a new app password for "Mail"
        const emailUser = this.configService.get<string>('email.user') || this.adminEmail;
        const emailPassword = this.configService.get<string>('email.password') || '';

        if (!emailPassword) {
            this.logger.warn('EMAIL_PASSWORD not configured. Email notifications will not work.');
            return;
        }

        this.transporter = nodemailer.createTransport({
            service: 'gmail',
            auth: {
                user: emailUser,
                pass: emailPassword,
            },
        });

        // Test connection
        this.transporter.verify((error) => {
            if (error) {
                this.logger.error('Email transporter verification failed:', error);
                this.logger.warn('Please check your EMAIL_USER and EMAIL_PASSWORD. Make sure you are using Gmail App Password.');
            } else {
                this.logger.log('Email transporter configured successfully');
            }
        });
    }

    /**
     * Send new order notification email
     */
    async sendNewOrderEmail(orderData: OrderEmailData): Promise<void> {
        try {
            const { orderId, orderNumber, customerName, phone, subtotal, shippingCost, tax, discount, total, amountPaid, items } = orderData;

            // Generate items list for plain text
            const itemsListText = items
                .map((item, index) => `${index + 1}. ${item.name} - Quantity: ${item.quantity} - Price: ${item.price} EGP - Total: ${item.totalPrice} EGP`)
                .join('\n');

            // Generate items list for HTML
            const itemsHtmlList = items
                .map((item, index) => `<li>${index + 1}. ${item.name} - Quantity: ${item.quantity} - Price: ${item.price} EGP - Total: ${item.totalPrice} EGP</li>`)
                .join('\n');

            // Arabic Subject
            const subjectAr = `🛍️ وصول طلب جديد – رقم الطلب: ${orderNumber}`;

            // English Subject
            const subjectEn = `🛍️ New Order Received – Order #${orderNumber}`;

            // Combined subject (both languages)
            const subject = `${subjectAr} / ${subjectEn}`;

            // Calculate totalPrice = subtotal + shipping
            const totalPrice = subtotal + shippingCost;

            // Plain text body (Arabic)
            const textBodyAr = `مرحبًا،

تم استلام طلب جديد الآن.

📋 معلومات الطلب:
🔹 رقم الطلب: ${orderNumber}
🔹 اسم العميل: ${customerName}
🔹 رقم الهاتف: ${phone}

💰 تفاصيل المبلغ:
🔹 المجموع الفرعي: ${subtotal.toFixed(2)} جنيه
🔹 الشحن: ${shippingCost.toFixed(2)} جنيه
🔹 الضريبة: ${tax.toFixed(2)} جنيه
${discount > 0 ? `🔹 الخصم: ${discount.toFixed(2)} جنيه\n` : ''}🔹 إجمالي المبلغ: ${totalPrice.toFixed(2)} جنيه
${amountPaid ? `🔹 المبلغ المدفوع: ${amountPaid.toFixed(2)} جنيه\n` : ''}🔹 المجموع الكلي: ${total.toFixed(2)} جنيه

🛒 تفاصيل المنتجات:
${itemsListText}

تم إرسال هذا التنبيه تلقائيًا لإعلامك بوجود طلب جديد.`;

            // Plain text body (English)
            const textBodyEn = `Hello,

A new order has just been received.

📋 Order Information:
🔹 Order ID: ${orderNumber}
🔹 Customer Name: ${customerName}
🔹 Phone: ${phone}

💰 Amount Details:
🔹 Subtotal: ${subtotal.toFixed(2)} EGP
🔹 Shipping: ${shippingCost.toFixed(2)} EGP
🔹 Tax: ${tax.toFixed(2)} EGP
${discount > 0 ? `🔹 Discount: ${discount.toFixed(2)} EGP\n` : ''}🔹 Total Amount: ${totalPrice.toFixed(2)} EGP
${amountPaid ? `🔹 Amount Paid: ${amountPaid.toFixed(2)} EGP\n` : ''}🔹 Grand Total: ${total.toFixed(2)} EGP

🛒 Items:
${itemsListText}

This notification was sent automatically to inform you about the new order.`;

            // HTML body
            const htmlBody = `
<!DOCTYPE html>
<html dir="rtl" lang="ar">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f4f4f4;
    }
    .container {
      background-color: #ffffff;
      padding: 30px;
      border-radius: 10px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
    h2 {
      color: #2c3e50;
      border-bottom: 3px solid #3498db;
      padding-bottom: 10px;
    }
    h3 {
      color: #34495e;
      margin-top: 20px;
      margin-bottom: 15px;
    }
    .order-info {
      background-color: #ecf0f1;
      padding: 20px;
      border-radius: 8px;
      margin: 15px 0;
    }
    .order-info strong {
      color: #2c3e50;
    }
    ul {
      list-style-type: none;
      padding: 0;
      margin: 0;
    }
    li {
      background-color: #f8f9fa;
      padding: 12px 15px;
      margin: 8px 0;
      border-radius: 6px;
      border-right: 4px solid #3498db;
      transition: all 0.2s ease;
    }
    li:hover {
      background-color: #e9ecef;
      transform: translateX(-3px);
    }
    .footer {
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid #ddd;
      color: #7f8c8d;
      font-size: 12px;
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="container">
    <h2>🛍️ وصول طلب جديد</h2>
    
    <p>تم استلام طلب جديد الآن.</p>
    
    <div class="order-info">
      <h3 style="margin-top: 0; color: #2c3e50;">📋 معلومات الطلب</h3>
      <p>
        <strong>رقم الطلب:</strong> ${orderNumber} <br/>
        <strong>اسم العميل:</strong> ${customerName} <br/>
        <strong>رقم الهاتف:</strong> ${phone}
      </p>
    </div>
    
    <div class="order-info" style="background-color: #e8f5e9; border-right: 4px solid #4caf50;">
      <h3 style="margin-top: 0; color: #2c3e50; margin-bottom: 15px;">💰 تفاصيل المبلغ</h3>
      <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
        <tr>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; color: #2c3e50; font-weight: 600;">المجموع الفرعي:</td>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; text-align: left; color: #1e293b;">${subtotal.toFixed(2)} ج.م</td>
        </tr>
        <tr>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; color: #2c3e50; font-weight: 600;">الشحن:</td>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; text-align: left; color: #1e293b;">${shippingCost.toFixed(2)} ج.م</td>
        </tr>
        <tr>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; color: #2c3e50; font-weight: 600;">الضريبة:</td>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; text-align: left; color: #1e293b;">${tax.toFixed(2)} ج.م</td>
        </tr>
        ${discount > 0 ? `
        <tr>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; color: #2c3e50; font-weight: 600;">الخصم:</td>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; text-align: left; color: #e74c3c;">-${discount.toFixed(2)} ج.م</td>
        </tr>
        ` : ''}
        <tr>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; color: #2c3e50; font-weight: 700;">إجمالي المبلغ:</td>
          <td style="padding: 10px 0; border-bottom: 1px solid #ddd; text-align: left; font-weight: 700; color: #1e293b;">${totalPrice.toFixed(2)} ج.م</td>
        </tr>
        ${amountPaid ? `
        <tr>
          <td style="padding: 10px 0; border-top: 2px solid #666; border-bottom: 2px solid #666; color: #2c3e50; font-weight: 700;">المبلغ المدفوع:</td>
          <td style="padding: 10px 0; border-top: 2px solid #666; border-bottom: 2px solid #666; text-align: left; font-weight: 700; color: #d4af37;">${amountPaid.toFixed(2)} ج.م</td>
        </tr>
        ` : ''}
        <tr>
          <td style="padding: 15px 0 10px 0; border-top: 2px solid #666; color: #2c3e50; font-weight: 700; font-size: 1.15em;">المجموع الكلي:</td>
          <td style="padding: 15px 0 10px 0; border-top: 2px solid #666; text-align: left; font-weight: 700; font-size: 1.15em; color: #d4af37;">${total.toFixed(2)} ج.م</td>
        </tr>
      </table>
    </div>
    
    <h3>🛒 تفاصيل المنتجات:</h3>
    <ul>
      ${itemsHtmlList}
    </ul>
    
    <div class="footer">
      <p>تم إرسال هذا الإشعار تلقائيًا لإعلامك بوصول طلب جديد.</p>
      <p>This notification was sent automatically to inform you about the new order.</p>
    </div>
  </div>
</body>
</html>`;

            // Check if transporter is initialized
            if (!this.transporter) {
                this.logger.warn('Email transporter not initialized. Skipping email send.');
                return;
            }

            // Send email
            const emailUser = this.configService.get<string>('email.user') || this.adminEmail;
            const mailOptions = {
                from: `${this.configService.get<string>('email.name')} `,
                to: this.adminEmail,
                subject: subject,
                text: `${textBodyAr}\n\n---\n\n${textBodyEn}`,
                html: htmlBody,
            };

            this.logger.log(`Attempting to send email for order ${orderNumber} to ${this.adminEmail}`);
            const info = await this.transporter.sendMail(mailOptions);
            this.logger.log(`✅ New order email sent successfully for order ${orderNumber}`);
            this.logger.log(`Email message ID: ${info.messageId}`);
            this.logger.log(`Email sent to: ${info.accepted.join(', ')}`);
        } catch (error) {
            this.logger.error(`❌ Failed to send new order email: ${error.message}`, error.stack);
            this.logger.error(`Error details:`, error);
            // Don't throw error to avoid breaking order creation
        }
    }
}

